import {
  TASK_TYPE_VIEW_SOURCE_ENTITY,
  type TaskTypeViewSourceKind,
} from '@erp/shared-types';
import { getValueAtPath, readEntityReference } from './taskTypeViewValue';

export type ViewSourceKind = TaskTypeViewSourceKind;

export type ResolvedViewSource =
  | { status: 'ready'; id: string }
  | {
      status: 'empty';
      reason: 'wrong-anchor' | 'missing-id';
      anchorType: string | null;
    };

/** Id for an email or document placement. Does not follow associations. */
export function resolveViewSource(input: {
  kind: ViewSourceKind;
  source: { from: 'anchor' } | { from: 'path'; path: readonly string[] };
  anchorType: string | null;
  anchorId: string | null;
  workingCopy: Record<string, unknown>;
}): ResolvedViewSource {
  if (input.source.from === 'path') {
    return pathSource(
      input.kind,
      input.workingCopy,
      input.source.path,
      input.anchorType,
    );
  }
  return anchorSource(input.kind, input.anchorType, input.anchorId);
}

/**
 * The field holds either the id itself or `{ entityType, entityId }` (a
 * `document` field, or an `entity_reference` wired from a workflow socket).
 * A reference to some other entity is not this viewer's id.
 */
function pathSource(
  kind: ViewSourceKind,
  workingCopy: Record<string, unknown>,
  path: readonly string[],
  anchorType: string | null,
): ResolvedViewSource {
  const id = sourceIdFromValue(kind, getValueAtPath(workingCopy, path));
  if (!id) return { status: 'empty', reason: 'missing-id', anchorType };
  return { status: 'ready', id };
}

function sourceIdFromValue(
  kind: ViewSourceKind,
  value: unknown,
): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  const reference = readEntityReference(value);
  if (
    !reference ||
    reference.entityType !== TASK_TYPE_VIEW_SOURCE_ENTITY[kind]
  ) {
    return null;
  }
  return reference.entityId;
}

function anchorSource(
  kind: ViewSourceKind,
  anchorType: string | null,
  anchorId: string | null,
): ResolvedViewSource {
  if (anchorType !== TASK_TYPE_VIEW_SOURCE_ENTITY[kind] || !anchorId) {
    return { status: 'empty', reason: 'wrong-anchor', anchorType };
  }
  return { status: 'ready', id: anchorId };
}
